/**
 * Admin Announcements — compose + send Web Push broadcast, read-only history.
 */
import {
  fetchAdminAnnouncements,
  sendAdminAnnouncement,
  type AdminAnnouncementRow,
} from "../lib/cloud";

function formatSentAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d.toISOString();
  }
}

export function mountAdminAnnouncements(root: HTMLElement): { refresh: () => void } {
  root.replaceChildren();

  const heading = document.createElement("h3");
  heading.className = "adminModerationHeading";
  heading.textContent = "Announcements";
  root.appendChild(heading);

  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.textContent =
    "Send a high-priority push to every player with notifications enabled and the Announcements category on. Opens Home when tapped.";
  root.appendChild(hint);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  const form = document.createElement("div");
  form.className = "modeBox";

  const titleRow = document.createElement("div");
  titleRow.className = "formRow";
  const titleLabel = document.createElement("span");
  titleLabel.className = "formLabel";
  titleLabel.textContent = "Title";
  const titleInput = document.createElement("input");
  titleInput.className = "formInput";
  titleInput.type = "text";
  titleInput.maxLength = 120;
  titleInput.placeholder = "Announcement title";
  titleInput.setAttribute("aria-label", "Announcement title");
  titleRow.append(titleLabel, titleInput);
  form.appendChild(titleRow);

  const bodyRow = document.createElement("div");
  bodyRow.className = "formRow";
  const bodyLabel = document.createElement("span");
  bodyLabel.className = "formLabel";
  bodyLabel.textContent = "Description";
  const bodyInput = document.createElement("textarea");
  bodyInput.className = "formInput";
  bodyInput.rows = 5;
  bodyInput.maxLength = 500;
  bodyInput.placeholder = "Announcement description";
  bodyInput.setAttribute("aria-label", "Announcement description");
  bodyRow.append(bodyLabel, bodyInput);
  form.appendChild(bodyRow);

  const actions = document.createElement("div");
  actions.className = "adminSplashActions";
  const sendBtn = document.createElement("button");
  sendBtn.type = "button";
  sendBtn.className = "btn green";
  sendBtn.textContent = "Send";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "btn";
  cancelBtn.textContent = "Cancel";
  actions.append(sendBtn, cancelBtn);
  form.appendChild(actions);
  root.appendChild(form);

  const historyHeading = document.createElement("h4");
  historyHeading.className = "adminPlayerStatsSubheading";
  historyHeading.textContent = "History";
  root.appendChild(historyHeading);

  const historyWrap = document.createElement("div");
  historyWrap.className = "leaderboardWrap";
  root.appendChild(historyWrap);

  let busy = false;
  let rows: AdminAnnouncementRow[] = [];

  function syncSendEnabled() {
    const ok = !!titleInput.value.trim() && !!bodyInput.value.trim() && !busy;
    sendBtn.disabled = !ok;
  }

  function clearForm() {
    titleInput.value = "";
    bodyInput.value = "";
    syncSendEnabled();
  }

  function renderHistory() {
    historyWrap.replaceChildren();
    if (!rows.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "No announcements sent yet.";
      historyWrap.appendChild(empty);
      return;
    }
    for (const row of rows) {
      const card = document.createElement("div");
      card.className = "modeBox";
      card.style.marginBottom = "10px";

      const t = document.createElement("strong");
      t.textContent = row.title;
      card.appendChild(t);

      const desc = document.createElement("p");
      desc.className = "accessHint";
      desc.style.margin = "6px 0";
      desc.textContent = row.body;
      card.appendChild(desc);

      const meta = document.createElement("p");
      meta.className = "accessHint";
      meta.style.margin = "0";
      const n = row.recipientCount;
      meta.textContent = `${formatSentAt(row.createdAt)} · Sent to ${n} user${n === 1 ? "" : "s"}`;
      card.appendChild(meta);

      historyWrap.appendChild(card);
    }
  }

  async function loadHistory() {
    status.textContent = "Loading…";
    try {
      rows = await fetchAdminAnnouncements();
      renderHistory();
      status.textContent = "";
    } catch (e) {
      status.textContent =
        e instanceof Error && e.message === "forbidden" ? "Forbidden." : "Could not load history.";
    }
  }

  titleInput.addEventListener("input", syncSendEnabled);
  bodyInput.addEventListener("input", syncSendEnabled);

  cancelBtn.onclick = () => {
    if (busy) return;
    clearForm();
    status.textContent = "";
  };

  sendBtn.onclick = async () => {
    const title = titleInput.value.trim();
    const body = bodyInput.value.trim();
    if (!title || !body || busy) return;
    busy = true;
    syncSendEnabled();
    cancelBtn.disabled = true;
    status.textContent = "Sending…";
    try {
      const res = await sendAdminAnnouncement({ title, body });
      const n = res.recipientCount;
      status.textContent = `Sent to ${n} user${n === 1 ? "" : "s"}.`;
      clearForm();
      rows = [res.announcement, ...rows.filter((r) => r.id !== res.announcement.id)];
      renderHistory();
    } catch (e) {
      status.textContent =
        e instanceof Error && e.message === "forbidden"
          ? "Forbidden."
          : e instanceof Error
            ? e.message
            : "Could not send announcement.";
    } finally {
      busy = false;
      cancelBtn.disabled = false;
      syncSendEnabled();
    }
  };

  syncSendEnabled();
  void loadHistory();

  return {
    refresh: () => {
      void loadHistory();
    },
  };
}
